/**
 * Ability and ultimate runtime: edge-triggered activation, gating (cooldown, energy, charge,
 * disabling effects, targeting), the step sequencer, and toggle stances.
 */
import { SIM_DT } from '../constants.js';
import { getAbility } from '../data/index.js';
import { groundBelow } from './deployables.js';
import { hasLineOfSight, runPrimitive } from './primitives.js';
import { aimDir, aimOrigin, centerOf, secondsToTicks } from './state.js';
import { defOf, emit, isLive, mech } from './tick.js';
import { otherSlot } from './types.js';
/** Farthest ground point an untargeted ability can be aimed at. */
const MAX_AIM_GROUND = 60;
export function abilityDefinitionOf(t, slot, index) {
    const def = defOf(t, slot);
    return getAbility(index === 0 ? def.abilities[0].id : def.ultimate);
}
/** The point on the arena the aim ray hits, or the far point projected onto the floor. */
export function aimGroundPoint(t, slot, maxRange = MAX_AIM_GROUND) {
    const c = mech(t, slot);
    const def = defOf(t, slot);
    const origin = aimOrigin(c.body, def, t.ctx.balance.aimHeightFraction);
    const dir = aimDir(c.body);
    const hit = t.world.castRayHit(origin, dir, maxRange);
    const d = hit ? hit.distance : maxRange;
    const point = { x: origin.x + dir.x * d, y: origin.y + dir.y * d, z: origin.z + dir.z * d };
    if (hit && hit.normal.y > 0.7)
        return point;
    return { x: point.x, y: groundBelow(t, point, 80), z: point.z };
}
function isToggleStance(def) {
    return def.steps.some((s) => s.primitive.type === 'stance' && s.primitive.mode === 'toggle');
}
function toggleKind(def) {
    for (const s of def.steps) {
        if (s.primitive.type === 'stance' && s.primitive.mode === 'toggle')
            return s.primitive.kind;
    }
    return null;
}
/** Captures what the ability aims at when it activates; null means it needs a target it lacks. */
function captureTarget(t, slot, def) {
    if (def.targetRange > 0) {
        const enemy = otherSlot(slot);
        const target = mech(t, enemy);
        if (!target.alive)
            return 'missing';
        const me = mech(t, slot).body.position;
        const dist = Math.hypot(target.body.position.x - me.x, target.body.position.y - me.y, target.body.position.z - me.z);
        if (dist > def.targetRange || !hasLineOfSight(t, slot))
            return 'missing';
        const c = centerOf(target.body, defOf(t, enemy));
        return { x: c.x, y: target.body.position.y, z: c.z };
    }
    return aimGroundPoint(t, slot);
}
function runDueSteps(t, slot, rt, def, input) {
    while (rt.step < def.steps.length) {
        const step = def.steps[rt.step];
        if (secondsToTicks(step.at) > rt.ticks)
            break;
        runPrimitive(t, slot, step.primitive, rt.target, input);
        rt.step++;
        if (!mech(t, slot).alive)
            break;
    }
    if (rt.step >= def.steps.length)
        rt.active = false;
}
function tryActivate(t, slot, index, rt, input) {
    const c = mech(t, slot);
    const m = t.mods[slot];
    if (!c.alive || !isLive(t) || m.noAbilities || m.stunned)
        return;
    const mechDef = defOf(t, slot);
    const def = abilityDefinitionOf(t, slot, index);
    if (index === 0) {
        if (rt.cooldown > 0 || c.body.energy < def.energyCost)
            return;
    }
    else if (c.ultCharge < 100) {
        return;
    }
    const toggle = toggleKind(def);
    if (toggle !== null && c.effects.some((e) => e.kind === toggle)) {
        // Pressing again ends a toggle stance; the step's own logic removes the effect.
        rt.active = true;
        rt.step = 0;
        rt.ticks = 0;
        rt.toggled = false;
        runDueSteps(t, slot, rt, def, input);
        rt.cooldown = index === 0 ? 1 : 0;
        return;
    }
    const target = captureTarget(t, slot, def);
    if (target === 'missing')
        return;
    rt.active = true;
    rt.step = 0;
    rt.ticks = 0;
    rt.target = target;
    if (index === 0) {
        rt.cooldown = mechDef.abilities[0].cooldown * m.cooldownScale;
        c.body.energy = Math.max(0, c.body.energy - def.energyCost);
        t.s.stats[slot].abilitiesUsed++;
    }
    else {
        c.ultCharge = 0;
        t.s.stats[slot].ultimatesUsed++;
    }
    emit(t, { t: 'ability', slot, index, id: def.id, position: { ...c.body.position } });
    runDueSteps(t, slot, rt, def, input);
    if (toggle !== null)
        rt.toggled = c.effects.some((e) => e.kind === toggle);
}
export function stepAbilities(t, slot, input) {
    const c = mech(t, slot);
    for (const index of [0, 1]) {
        const rt = c.abilities[index];
        const pressed = index === 0 ? input.ability : input.ultimate;
        const edge = pressed && !rt.held;
        rt.held = pressed;
        if (index === 0 && rt.cooldown > 0)
            rt.cooldown = Math.max(0, rt.cooldown - SIM_DT);
        if (rt.active) {
            rt.ticks++;
            if (c.alive && isLive(t))
                runDueSteps(t, slot, rt, abilityDefinitionOf(t, slot, index), input);
            else
                rt.active = false;
        }
        if (edge)
            tryActivate(t, slot, index, rt, input);
        const def = abilityDefinitionOf(t, slot, index);
        if (isToggleStance(def)) {
            const kind = toggleKind(def);
            rt.toggled = c.effects.some((e) => e.kind === kind);
        }
    }
}
//# sourceMappingURL=abilities.js.map